import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../../../services/api';
import { io } from 'socket.io-client';

// Import widgets
import CaseloadOverviewWidget from './CaseloadOverviewWidget';
import PendingActionsWidget from './PendingActionsWidget';
import QuickActionsGrid from './QuickActionsGrid';
import RecentActivityWidget from './RecentActivityWidget';
import InboxWidget from './InboxWidget';
// import AtRiskClientsWidget from './AtRiskClientsWidget'; // Replaced by TASS PrioritizedCaseloadWidget
import ConsultationSummaryWidget from './ConsultationSummaryWidget';
import WeeklyProgressWidget from './WeeklyProgressWidget';
import TriageReviewWidget from './TriageReviewWidget';
import ComplianceTrackingWidget from './ComplianceTrackingWidget';
import SupervisorCommunicationWidget from './SupervisorCommunicationWidget';
import SessionSchedulingWidget from './SessionSchedulingWidget';

// TASS widgets
import PriorityBriefCard from '../tass/PriorityBriefCard';
import PrioritizedCaseloadWidget from '../tass/PrioritizedCaseloadWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

function TherapistDashboardHome({ token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dashboard data states
  const [summary, setSummary] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [atRiskClients, setAtRiskClients] = useState([]);
  const [weeklyProgress, setWeeklyProgress] = useState(null);
  const [triageReviews, setTriageReviews] = useState([]);
  const [complianceStatus, setComplianceStatus] = useState([]);
  const [supervisorCommunications, setSupervisorCommunications] = useState([]);
  const [consultationSummary, setConsultationSummary] = useState(null);

  // Socket.io for real-time updates
  const [socket, setSocket] = useState(null);

  // Load all dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch all dashboard data in parallel
      const [
        summaryRes,
        activityRes,
        atRiskRes,
        progressRes,
        triageRes,
        complianceRes,
        supervisorRes,
        consultationRes
      ] = await Promise.all([
        api.get('/therapist/dashboard/summary'),
        api.get('/therapist/dashboard/recent-activity?limit=20'),
        api.get('/therapist/dashboard/at-risk-clients'),
        api.get('/therapist/dashboard/weekly-progress'),
        api.get('/therapist/dashboard/triage-reviews'),
        api.get('/therapist/dashboard/compliance-status'),
        api.get('/therapist/dashboard/supervisor-communications'),
        api.get('/therapist/dashboard/consultation-summary')
      ]);

      setSummary(summaryRes.data);
      setRecentActivity(activityRes.data);
      setAtRiskClients(atRiskRes.data);
      setWeeklyProgress(progressRes.data);
      setTriageReviews(triageRes.data);
      setComplianceStatus(complianceRes.data);
      setSupervisorCommunications(supervisorRes.data);
      setConsultationSummary(consultationRes.data);

    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.response?.data?.msg || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  // Set up Socket.io for real-time updates
  useEffect(() => {
    const newSocket = io(BACKEND_URL, {
      auth: { token }
    });

    newSocket.on('connect', () => {
      console.log('Dashboard socket connected');
    });

    // Listen for relevant events
    newSocket.on('client:activity', () => {
      // Refresh activity feed
      api.get('/therapist/dashboard/recent-activity?limit=20')
        .then(res => setRecentActivity(res.data))
        .catch(err => console.error('Error refreshing activity:', err));
    });

    newSocket.on('alert:created', () => {
      // Refresh summary and at-risk clients
      api.get('/therapist/dashboard/summary')
        .then(res => setSummary(res.data))
        .catch(err => console.error('Error refreshing summary:', err));

      api.get('/therapist/dashboard/at-risk-clients')
        .then(res => setAtRiskClients(res.data))
        .catch(err => console.error('Error refreshing at-risk:', err));
    });

    newSocket.on('checkin:created', () => {
      // Refresh weekly progress and at-risk
      api.get('/therapist/dashboard/weekly-progress')
        .then(res => setWeeklyProgress(res.data))
        .catch(err => console.error('Error refreshing progress:', err));

      api.get('/therapist/dashboard/at-risk-clients')
        .then(res => setAtRiskClients(res.data))
        .catch(err => console.error('Error refreshing at-risk:', err));
    });

    newSocket.on('goal:completed', () => {
      // Refresh weekly progress
      api.get('/therapist/dashboard/weekly-progress')
        .then(res => setWeeklyProgress(res.data))
        .catch(err => console.error('Error refreshing progress:', err));
    });

    newSocket.on('consultation:created', () => {
      // Refresh consultation summary
      api.get('/therapist/dashboard/consultation-summary')
        .then(res => setConsultationSummary(res.data))
        .catch(err => console.error('Error refreshing consultations:', err));
    });

    newSocket.on('supervisor:intervention', () => {
      // Refresh supervisor communications
      api.get('/therapist/dashboard/supervisor-communications')
        .then(res => setSupervisorCommunications(res.data))
        .catch(err => console.error('Error refreshing supervisor comms:', err));
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [token]);

  // Initial load
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleRefresh = () => {
    loadDashboard();
  };

  const handleClientClick = (clientId) => {
    navigate(`/therapist?view=clients&clientId=${clientId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={handleRefresh} startIcon={<RefreshIcon />} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom display="flex" alignItems="center" gap={1}>
            <DashboardIcon fontSize="large" color="primary" />
            Therapist Dashboard (Draft)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back! Here's your caseload overview.
          </Typography>
        </Box>
        <Tooltip title="Refresh dashboard">
          <IconButton onClick={handleRefresh} color="primary" aria-label="Refresh dashboard">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* TASS Priority Brief */}
      <PriorityBriefCard token={token} onClientClick={handleClientClick} />

      {/* Caseload Overview Cards */}
      <CaseloadOverviewWidget summary={summary} />

      {/* Quick Actions */}
      <Box mt={3}>
        <QuickActionsGrid navigate={navigate} />
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={3} mt={1}>
        {/* Left Column */}
        <Grid item xs={12} md={6}>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Inbox — non-interrupt notices. Crisis alerts are NOT here: they
                keep the CareNoteAlerts banner above the dashboard and their own
                acknowledgement path. Placed first in the column because it is
                the thing with outstanding items, not because it is the most
                urgent; the most urgent never reaches this list. */}
            <InboxWidget
              token={token}
              onOpenClient={handleClientClick}
            />

            {/* Recent Activity */}
            <RecentActivityWidget
              activities={recentActivity}
              onClientClick={handleClientClick}
            />

            {/* TASS Prioritized Caseload (replaces AtRiskClientsWidget) */}
            <PrioritizedCaseloadWidget
              token={token}
              onClientClick={handleClientClick}
            />
            {/* AtRiskClientsWidget replaced by PrioritizedCaseloadWidget above
            <AtRiskClientsWidget
              atRiskClients={atRiskClients}
              onClientClick={handleClientClick}
            />
            */}

            {/* Triage Reviews */}
            <TriageReviewWidget
              triageReviews={triageReviews}
              onClientClick={handleClientClick}
            />
          </Box>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={6}>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Pending Actions */}
            <PendingActionsWidget
              summary={summary}
              navigate={navigate}
            />

            {/* Consultation Summary */}
            <ConsultationSummaryWidget
              consultationSummary={consultationSummary}
              navigate={navigate}
            />

            {/* Weekly Progress */}
            <WeeklyProgressWidget
              weeklyProgress={weeklyProgress}
            />

            {/* Compliance Tracking */}
            <ComplianceTrackingWidget
              complianceStatus={complianceStatus}
              onClientClick={handleClientClick}
            />

            {/* Supervisor Communications */}
            <SupervisorCommunicationWidget
              communications={supervisorCommunications}
              onClientClick={handleClientClick}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Session Scheduling (Placeholder) */}
      <Box mt={3}>
        <SessionSchedulingWidget />
      </Box>
    </Box>
  );
}

export default TherapistDashboardHome;
